import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import Layout from '../../components/Layout';
import { fmtDateTime } from '../../utils/dateFormat';
import useAuthStore from '../../store/authStore';
import { confirm } from '../../components/ConfirmDialog';

const bakuISO = (d) => d.toLocaleDateString('en-CA', { timeZone: 'Asia/Baku' });
const todayStr = () => bakuISO(new Date());
const yesterdayStr = () => { const d = new Date(); d.setDate(d.getDate() - 1); return bakuISO(d); };

const fmt = (v) => Number(v || 0).toFixed(2);

const roleLabel = (r) => {
    const v = String(r || '').toLowerCase();
    if (v.includes('admin') && v.includes('kassir')) return 'Admin/Kassir';
    if (v === 'admin' || v === 'superadmin') return 'Admin';
    if (v === 'kassir') return 'Kassir';
    if (v === 'waiter') return 'Ofisiant';
    return r || '—';
};

export default function CancelledOrders() {
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const isAdmin = ['admin', 'superadmin'].includes(String(user?.role || '').toLowerCase()) || !!user?.is_manager;

    const [from, setFrom] = useState(todayStr());
    const [to,   setTo]   = useState(todayStr());
    const [rows,    setRows]    = useState([]);
    const [loading, setLoading] = useState(false);

    const [refunding, setRefunding] = useState(false);
    const [tablePopup, setTablePopup] = useState(null); // { orderId, tables: [{id,name}] }

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/orders/cancelled', { params: { from, to } });
            setRows(res.data.data || []);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Yüklənmədi.');
        } finally {
            setLoading(false);
        }
    }, [from, to]);

    useEffect(() => { load(); }, [load]);

    const setQuick = (which) => {
        const d = which === 'today' ? todayStr() : yesterdayStr();
        setFrom(d); setTo(d);
    };

    const isTodayFilter = from === todayStr() && to === todayStr();
    const isYesterdayFilter = from === yesterdayStr() && to === yesterdayStr();

    const doRestore = async (orderId, targetTableId = 0) => {
        if (!isAdmin) return toast.error('Yalnız admin geri qaytara bilər.');
        if (!targetTableId && !(await confirm(`Sifariş #${orderId} geri masaya açılsın? Ləğv geri götürüləcək, sifariş yenidən aktiv olacaq.`))) return;
        setRefunding(true);
        try {
            const res = await api.post(`/orders/${orderId}/restore-cancelled`, targetTableId ? { target_table_id: targetTableId } : {});
            if (res.data?.need_table) {
                setTablePopup({ orderId, tables: res.data.free_tables || [] });
                return;
            }
            toast.success(res.data?.msg || 'Geri açıldı.');
            setTablePopup(null);
            navigate(`/waiter/order/${res.data?.order_id || orderId}`);
        } catch (e) {
            toast.error(e.response?.data?.msg || 'Geri qaytarılmadı.');
        } finally {
            setRefunding(false);
        }
    };

    const inputCls = 'px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-rose-400 transition-all bg-white';
    const quickCls = (active) => `px-4 py-2 rounded-xl text-sm font-bold transition-all border ${active ? 'bg-rose-600 text-white border-rose-600' : 'bg-white text-gray-500 border-gray-200 hover:border-rose-300'}`;

    return (
        <Layout>
            <div>
                <div className="mb-5">
                    <h1 className="text-xl font-black text-gray-800">🗑️ Silinmiş hesablar</h1>
                    <p className="text-sm text-gray-400 mt-0.5">Ləğv edilmiş sifarişlər — geri masaya aça bilərsiniz</p>
                </div>

                {/* Filtrlər */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <button onClick={() => setQuick('today')} className={quickCls(isTodayFilter)}>Bugün</button>
                    <button onClick={() => setQuick('yesterday')} className={quickCls(isYesterdayFilter)}>Dünən</button>
                    <span className="mx-1 h-6 w-px bg-gray-200" />
                    <input type="date" value={from} onChange={e => setFrom(e.target.value)} className={inputCls} />
                    <span className="text-gray-400 text-sm">—</span>
                    <input type="date" value={to} onChange={e => setTo(e.target.value)} className={inputCls} />
                    <button onClick={load}
                        className="px-4 py-2 rounded-xl text-sm font-bold text-white bg-rose-600 hover:bg-rose-700 transition-all">
                        Göstər
                    </button>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    {loading ? (
                        <div className="p-12 text-center text-gray-400">Yüklənir...</div>
                    ) : rows.length === 0 ? (
                        <div className="p-12 text-center text-gray-400">Bu aralıqda ləğv edilmiş sifariş yoxdur.</div>
                    ) : (
                        <>
                            {/* ── Mobil: kartlar ── */}
                            <div className="divide-y divide-gray-100 sm:hidden">
                                {rows.map(r => (
                                    <div key={r.order_id} className="p-4">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <div className="text-sm font-black text-gray-800">
                                                    #{r.order_id}
                                                    <span className="ml-2 font-semibold text-gray-500">{r.table_name || '—'}</span>
                                                </div>
                                                <div className="mt-0.5 text-[11px] text-gray-400 font-mono">{fmtDateTime(r.cancelled_at)}</div>
                                            </div>
                                            <div className="text-sm font-mono font-black text-gray-800 whitespace-nowrap">{fmt(r.amount)} ₼</div>
                                        </div>
                                        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                                            <span>👤 {r.waiter_name || '—'}{r.waiter_title ? ` · ${r.waiter_title}` : ''}</span>
                                            <span>❌ {r.cancelled_by_name || '—'}{r.cancelled_by_role ? ` (${roleLabel(r.cancelled_by_role)})` : ''}</span>
                                        </div>
                                        {isAdmin && (
                                            <button
                                                onClick={() => doRestore(r.order_id)}
                                                disabled={refunding}
                                                className="mt-3 w-full rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-bold text-emerald-600 hover:bg-emerald-100 transition-all disabled:opacity-50"
                                            >
                                                ↩ Geri qaytar
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* ── Desktop: cədvəl ── */}
                            <div className="hidden overflow-x-auto sm:block"><table className="w-full min-w-[760px]">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Ləğv vaxtı</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Sifariş</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Masa</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Ofisiant</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Məbləğ</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Ləğv edən</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Əməliyyat</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(r => (
                                        <tr key={r.order_id} className="border-t border-gray-50 hover:bg-gray-50">
                                            <td className="px-4 py-3 text-xs text-gray-400 font-mono whitespace-nowrap">{fmtDateTime(r.cancelled_at)}</td>
                                            <td className="px-4 py-3 text-sm font-semibold text-gray-800">#{r.order_id}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600">{r.table_name || '—'}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600">
                                                {r.waiter_name || '—'}
                                                {r.waiter_title ? <span className="text-gray-400"> · {r.waiter_title}</span> : ''}
                                            </td>
                                            <td className="px-4 py-3 text-sm font-mono font-bold text-gray-800">{fmt(r.amount)} ₼</td>
                                            <td className="px-4 py-3 text-sm text-gray-600">
                                                {r.cancelled_by_name || '—'}
                                                {r.cancelled_by_role ? <span className="text-gray-400"> ({roleLabel(r.cancelled_by_role)})</span> : ''}
                                            </td>
                                            <td className="px-4 py-3">
                                                {isAdmin && (
                                                    <button
                                                        onClick={() => doRestore(r.order_id)}
                                                        disabled={refunding}
                                                        title="Ləğvi geri götür — masaya aç"
                                                        className="px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100 transition-all disabled:opacity-50"
                                                    >
                                                        ↩ Geri qaytar
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table></div>
                        </>
                    )}
                </div>
            </div>

            {/* Geri qaytar — masa seçimi (orijinal masa doludursa) */}
            {tablePopup && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                     onClick={e => e.target === e.currentTarget && setTablePopup(null)}>
                    <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
                        <div className="border-b border-gray-100 p-5">
                            <h3 className="text-lg font-bold text-gray-800">↩ Boş masa seç</h3>
                            <p className="mt-1 text-sm text-gray-400">Orijinal masa doludur. Sifariş #{tablePopup.orderId} hansı masaya açılsın?</p>
                        </div>
                        <div className="max-h-80 overflow-y-auto p-4">
                            {tablePopup.tables.length === 0 ? (
                                <p className="py-6 text-center text-sm text-gray-400">Boş masa yoxdur.</p>
                            ) : (
                                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                                    {tablePopup.tables.map(t => (
                                        <button key={t.id} type="button"
                                            disabled={refunding}
                                            onClick={() => doRestore(tablePopup.orderId, t.id)}
                                            className="rounded-xl border-2 border-gray-200 px-3 py-3 text-sm font-bold text-gray-700 hover:border-emerald-400 hover:bg-emerald-50 transition-all disabled:opacity-50">
                                            {t.name}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="flex justify-end border-t border-gray-100 p-4">
                            <button type="button" onClick={() => setTablePopup(null)}
                                className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-500 hover:bg-gray-50">
                                Ləğv et
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
}
